import { array, get, fn } from '@ember/helper';
import Button from 'tio-ui/components/button';
import EmberFlatpickr from 'ember-flatpickr/components/ember-flatpickr';
import Input from 'tio-ui/components/input';
import Form from 'tio-ui/components/form';
import type { FieldValue, FormErrors, FormResult } from 'tio-ui/components/form';
import { formatDateToISO } from 'tio-common/utils/date';
import Select from 'tio-ui/components/select';
import { on } from '@ember/modifier';
import { or, join } from 'tio-ui/utilities';
import { PhCalendarBlank } from 'ember-phosphor-icons';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedObject } from '@ember/reactive/collections';
import { VStack, Section } from 'tio-ui/components/layout';
import * as v from 'valibot';
import Component from '@glimmer/component';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type RouterService from '@ember/routing/router';
import { addRelatedRecordsRequest, saveRecord } from 'tio-data/requests/save-record';
import type { Store } from '@warp-drive/core';

export interface EmployeesCreateEmployeeSignature {
  Args: {
    model: EmployeeModel;
  };
}

// Validation schema for employee creation form
const phoneRegExp =
  /^((\+[1-9]{1,4}[ -]*)|(\([0-9]{2,3}\)[ -]*)|([0-9]{2,4})[ -]*)*?[0-9]{3,4}?[ -]*[0-9]{3,4}?$/;

const employeeFormSchema = v.object({
  firstName: v.pipe(v.string('First name is required'), v.nonEmpty('First name is required')),
  lastName: v.pipe(v.string('Last name is required'), v.nonEmpty('Last name is required')),
  email: v.pipe(
    v.string('E-mail is required'),
    v.nonEmpty('E-mail is required'),
    v.email('Please enter a valid email address')
  ),
  payrollId: v.pipe(v.string('Payroll ID is required'), v.nonEmpty('Payroll ID is required')),
  phoneNumber: v.optional(
    v.union([
      v.pipe(v.string(), v.length(0)),
      v.pipe(v.string(), v.regex(phoneRegExp, 'Please enter a valid phone number')),
    ])
  ),
  employmentType: v.optional(v.string()),
  birthYear: v.optional(v.string()),
  addressLine1: v.optional(v.string()),
  addressLine2: v.optional(v.string()),
  city: v.optional(v.string()),
  state: v.optional(v.string()),
  postalCode: v.optional(v.string()),
  employmentStartDate: v.optional(v.string()),
  employmentEndDate: v.optional(v.string()),
  salary: v.optional(v.string()),
  ratePeriod: v.optional(v.string()),
  title: v.optional(v.string()),
  department: v.optional(v.string()),
  costCenter: v.optional(v.string()),
  eligibilityStartDate: v.optional(v.string()),
  eligibilityEndDate: v.optional(v.string()),
});

/** Valibot → Form validate bridge */
const validate = (data: Record<string, unknown>): FormErrors => {
  const result = v.safeParse(employeeFormSchema, data);
  if (result.success) return {};
  return v.flatten(result.issues).nested ?? {};
};

export default class EmployeesCreateEmployeeComponent extends Component<EmployeesCreateEmployeeSignature> {
  @service declare store: Store;
  @service declare router: RouterService;

  @tracked planId: string | null = null;

  // Plain tracked object for Form@data — Ember Data models can't be JSON.stringify'd
  // due to circular relationship references. We initialize from the model and
  // sync back on submit.
  #formData?: Record<string, string>;

  get formData() {
    if (!this.#formData) {
      const m = this.args.model;
      this.#formData = trackedObject({
        firstName: m.firstName ?? '',
        lastName: m.lastName ?? '',
        email: m.email ?? '',
        payrollId: m.payrollId ?? '',
        phoneNumber: m.phoneNumber ?? '',
        employmentType: m.employmentType ?? '',
        birthYear: m.birthYear ?? '',
        addressLine1: m.addressLine1 ?? '',
        addressLine2: m.addressLine2 ?? '',
        city: m.city ?? '',
        state: m.state ?? '',
        postalCode: m.postalCode ?? '',
        employmentStartDate: m.employmentStartDate ?? '',
        employmentEndDate: m.employmentEndDate ?? '',
        salary: m.salary ?? '',
        ratePeriod: m.ratePeriod ?? '',
        title: m.title ?? '',
        department: m.department ?? '',
        costCenter: m.costCenter ?? '',
        eligibilityStartDate: m.eligibilityStartDate ?? '',
        eligibilityEndDate: m.eligibilityEndDate ?? '',
      });
    }
    return this.#formData;
  }

  // Cache for tracked customAttributes - initialized lazily
  #customAttributes?: Record<string, unknown>;

  get customAttributes(): Record<string, unknown> {
    if (!this.#customAttributes) {
      this.#customAttributes = trackedObject(
        (this.args.model.customAttributes as Record<string, unknown>) ?? {}
      );
    }
    return this.#customAttributes;
  }

  // US States list
  states = [
    'Alabama',
    'Alaska',
    'American Samoa',
    'Arizona',
    'Arkansas',
    'California',
    'Colorado',
    'Connecticut',
    'Delaware',
    'District of Columbia',
    'Florida',
    'Georgia',
    'Guam',
    'Hawaii',
    'Idaho',
    'Illinois',
    'Indiana',
    'Iowa',
    'Kansas',
    'Kentucky',
    'Louisiana',
    'Maine',
    'Marshall Islands',
    'Maryland',
    'Massachusetts',
    'Michigan',
    'Minnesota',
    'Mississippi',
    'Missouri',
    'Montana',
    'Nebraska',
    'Nevada',
    'New Hampshire',
    'New Jersey',
    'New Mexico',
    'New York',
    'North Carolina',
    'North Dakota',
    'Ohio',
    'Oklahoma',
    'Oregon',
    'Pennsylvania',
    'Puerto Rico',
    'Rhode Island',
    'South Carolina',
    'South Dakota',
    'Tennessee',
    'Texas',
    'Utah',
    'Vermont',
    'Virgin Island',
    'Virginia',
    'Washington',
    'West Virginia',
    'Wisconsin',
    'Wyoming',
  ];

  get birthYears(): string[] {
    const currentYear = new Date().getFullYear();
    const years: string[] = [];
    let startYear = 1920;

    while (startYear <= currentYear) {
      years.push(String(startYear++));
    }
    return years.reverse();
  }

  onSelectPlan = (planId: string) => {
    this.planId = planId || null;
  };

  /** Wraps EmberFlatpickr's Date[] callback into Form's FieldValue */
  onDateChange = (onChange: (value: FieldValue) => void, selectedDates: Date[]) => {
    if (selectedDates.length > 0 && selectedDates[0]) {
      onChange(formatDateToISO(selectedDates[0]));
    }
  };

  saveCustomAttr = (key: string, value: string) => {
    this.customAttributes[key] = value;
  };

  onInvalidSubmit = () => {
    setTimeout(() => {
      const firstErrorInput = document.querySelector(
        '[aria-invalid="true"], [data-has-errors="true"]'
      ) as HTMLElement | null;
      if (firstErrorInput) {
        firstErrorInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstErrorInput.focus();
      }
    }, 100);
  };

  onFormSubmit = async (result: FormResult<Record<string, unknown>>) => {
    try {
      // Sync form data back to the Ember Data model
      const data = result.data;
      const model = this.args.model;
      for (const [key, value] of Object.entries(data)) {
        (model as unknown as Record<string, unknown>)[key] = value || undefined;
      }

      // Copy tracked customAttributes back to the model before saving
      model.customAttributes = { ...this.customAttributes };
      const { content } = (await saveRecord(this.store, model)) as {
        content: { data: EmployeeModel };
      };
      const newEmployee = content.data;

      // Patch plan association if a plan is selected
      if (this.planId) {
        const plan = this.args.model.company.plans.find((p) => p.id === this.planId);
        if (plan) {
          plan.employees.push(newEmployee);
          // WarpDrive relationship-mutation builder (Phase 2.7) — replaces
          // plan.save({ adapterOptions: { createHasMany: [newEmployee] } }). The
          // legacy ApplicationAdapter#updateRecord translated `createHasMany`
          // into POST /plans/:id/relationships/employees with a JSON:API
          // resource-linkage array body; addRelatedRecordsRequest emits the
          // byte-identical request.
          await addRelatedRecordsRequest(this.store, plan, [newEmployee]);
        }
      }

      this.router.transitionTo('authenticated.admin.employees.show', newEmployee.id);
    } catch (error) {
      console.error('Failed to create employee:', error);
      const model = this.args.model;
      model.rollbackAttributes();

      // Re-sync formData from the rolled-back model so the UI stays consistent
      const formData = this.formData;
      for (const key of Object.keys(formData)) {
        formData[key] = (model as unknown as Record<string, string>)[key] ?? '';
      }
      this.#customAttributes = undefined;
    }
  };

  cancel = () => {
    this.router.transitionTo('authenticated.admin.employees.index');
  };

  <template>
    <Form
      @data={{this.formData}}
      @validate={{validate}}
      @onSubmit={{this.onFormSubmit}}
      @onInvalidSubmit={{this.onInvalidSubmit}}
      as |form|
    >
      <VStack>
        {{! Basic Information Section }}
        <Section>
          <:header>{{t "partner.employee.basic_info" default="Basic Information"}}</:header>
          <:body>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {{#let (form.field "firstName") as |f|}}
                <Input
                  @label={{t "partner.employee.first_name"}}
                  @value={{f.value}}
                  @onChange={{f.onChange}}
                  @onBlur={{f.onBlur}}
                  @errors={{f.errors}}
                  name="firstName"
                />
              {{/let}}
              {{#let (form.field "lastName") as |f|}}
                <Input
                  @label={{t "partner.employee.last_name"}}
                  @value={{f.value}}
                  @onChange={{f.onChange}}
                  @onBlur={{f.onBlur}}
                  @errors={{f.errors}}
                  name="lastName"
                />
              {{/let}}
              {{#let (form.field "payrollId") as |f|}}
                <Input
                  @label={{t "partner.employee.payroll_id"}}
                  @value={{f.value}}
                  @onChange={{f.onChange}}
                  @onBlur={{f.onBlur}}
                  @errors={{f.errors}}
                  name="payrollId"
                />
              {{/let}}
              {{#let (form.field "employmentType") as |f|}}
                <Select
                  @label={{t "partner.employee.employment_type"}}
                  @items={{array "FULL_TIME" "PART_TIME" "EXEMPT" "NON_EXEMPT"}}
                  {{! @glint-expect-error: FieldValue includes number but this field is always string }}
                  @selected={{f.value}}
                  @onChange={{f.onChange}}
                  @errors={{f.errors}}
                />
              {{/let}}
              {{#let (form.field "birthYear") as |f|}}
                <Select
                  @searchable={{true}}
                  @label={{t "partner.employee.birth_year"}}
                  @items={{this.birthYears}}
                  {{! @glint-expect-error: FieldValue includes number but this field is always string }}
                  @selected={{f.value}}
                  @onChange={{f.onChange}}
                  @errors={{f.errors}}
                />
              {{/let}}
              {{#let (form.field "phoneNumber") as |f|}}
                <Input
                  @label={{t "partner.employee.phone_number"}}
                  @value={{f.value}}
                  @onChange={{f.onChange}}
                  @onBlur={{f.onBlur}}
                  @errors={{f.errors}}
                  name="phoneNumber"
                  type="tel"
                />
              {{/let}}
              {{#let (form.field "email") as |f|}}
                <Input
                  @label={{t "partner.employee.email"}}
                  @value={{f.value}}
                  @onChange={{f.onChange}}
                  @onBlur={{f.onBlur}}
                  @errors={{f.errors}}
                  @type="email"
                  name="email"
                />
              {{/let}}
              <Select
                @label={{t "partner.employee.plan" default="Plan"}}
                @items={{@model.company.plans}}
                @selected={{this.planId}}
                @onChange={{this.onSelectPlan}}
              />
            </div>
          </:body>
        </Section>

        {{! Address Section }}
        <Section>
          <:header>{{t "partner.employee.address" default="Address"}}</:header>
          <:body>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {{#let (form.field "addressLine1") as |f|}}
                <Input
                  @label={{t "partner.employee.address_line1"}}
                  @value={{f.value}}
                  @onChange={{f.onChange}}
                  @onBlur={{f.onBlur}}
                  @errors={{f.errors}}
                  name="addressLine1"
                />
              {{/let}}
              {{#let (form.field "addressLine2") as |f|}}
                <Input
                  @label={{t "partner.employee.address_line2"}}
                  @value={{f.value}}
                  @onChange={{f.onChange}}
                  @onBlur={{f.onBlur}}
                  @errors={{f.errors}}
                  name="addressLine2"
                />
              {{/let}}
              {{#let (form.field "city") as |f|}}
                <Input
                  @label={{t "partner.employee.city"}}
                  @value={{f.value}}
                  @onChange={{f.onChange}}
                  @onBlur={{f.onBlur}}
                  @errors={{f.errors}}
                  name="city"
                />
              {{/let}}
              {{#let (form.field "state") as |f|}}
                <Select
                  data-test-state-select
                  @label={{t "partner.employee.state"}}
                  @items={{this.states}}
                  {{! @glint-expect-error: FieldValue includes number but this field is always string }}
                  @selected={{f.value}}
                  @onChange={{f.onChange}}
                  @searchable={{true}}
                  @errors={{f.errors}}
                />
              {{/let}}
              {{#let (form.field "postalCode") as |f|}}
                <Input
                  @label={{t "partner.employee.postal_code"}}
                  @value={{f.value}}
                  @onChange={{f.onChange}}
                  @onBlur={{f.onBlur}}
                  @errors={{f.errors}}
                  name="postalCode"
                />
              {{/let}}
            </div>
          </:body>
        </Section>

        {{! Employment Details Section }}
        <Section>
          <:header>{{t
              "partner.employee.employment_details"
              default="Employment Details"
            }}</:header>
          <:body>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {{#let (form.field "employmentStartDate") as |f|}}
                <div class="tio-col-extra-tight">
                  <label class="tio-text-sm-medium tio-fg-primary">{{t
                      "common.employment_start_date"
                    }}</label>
                  <div class="relative">
                    {{! @glint-expect-error: EmberFlatpickr type overrides}}
                    <EmberFlatpickr
                      @date={{or f.value ""}}
                      @onChange={{fn this.onDateChange f.onChange}}
                      @dateFormat="Y-m-d"
                      @altInput={{true}}
                      @altFormat="F j, Y"
                      @disableMobile={{true}}
                      placeholder={{t "common.date_placeholder"}}
                      class="tio-form-input"
                    />
                    <div
                      class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none tio-fg-disabled"
                    >
                      <PhCalendarBlank @size="20" />
                    </div>
                  </div>
                  {{#if f.errors.length}}
                    <p class="text-sm tio-fg-danger-h">{{join f.errors ", "}}</p>
                  {{/if}}
                </div>
              {{/let}}
              {{#let (form.field "employmentEndDate") as |f|}}
                <div class="tio-col-extra-tight">
                  <label class="tio-text-sm-medium tio-fg-primary">{{t
                      "common.employment_end_date"
                    }}</label>
                  <div class="relative">
                    {{! @glint-expect-error: EmberFlatpickr type overrides}}
                    <EmberFlatpickr
                      @date={{or f.value ""}}
                      @onChange={{fn this.onDateChange f.onChange}}
                      @dateFormat="Y-m-d"
                      @altInput={{true}}
                      @altFormat="F j, Y"
                      @disableMobile={{true}}
                      placeholder={{t "common.date_placeholder"}}
                      class="tio-form-input"
                    />
                    <div
                      class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none tio-fg-disabled"
                    >
                      <PhCalendarBlank @size="20" />
                    </div>
                  </div>
                  {{#if f.errors.length}}
                    <p class="text-sm tio-fg-danger-h">{{join f.errors ", "}}</p>
                  {{/if}}
                </div>
              {{/let}}
              {{#let (form.field "salary") as |f|}}
                <Input
                  @label={{t "partner.employee.salary"}}
                  @value={{f.value}}
                  @onChange={{f.onChange}}
                  @onBlur={{f.onBlur}}
                  @errors={{f.errors}}
                  name="salary"
                />
              {{/let}}
              {{#let (form.field "ratePeriod") as |f|}}
                <Select
                  @label={{t "partner.employee.rate_period"}}
                  @items={{array "HOURLY" "WEEKLY" "BI_WEEKLY" "MONTHLY" "YEARLY"}}
                  {{! @glint-expect-error: FieldValue includes number but this field is always string }}
                  @selected={{f.value}}
                  @onChange={{f.onChange}}
                  @errors={{f.errors}}
                />
              {{/let}}
              {{#let (form.field "title") as |f|}}
                <Input
                  @label={{t "partner.employee.title"}}
                  @value={{f.value}}
                  @onChange={{f.onChange}}
                  @onBlur={{f.onBlur}}
                  @errors={{f.errors}}
                  name="title"
                />
              {{/let}}
              {{#let (form.field "department") as |f|}}
                <Input
                  @label={{t "partner.employee.department"}}
                  @value={{f.value}}
                  @onChange={{f.onChange}}
                  @onBlur={{f.onBlur}}
                  @errors={{f.errors}}
                  name="department"
                />
              {{/let}}
              {{#let (form.field "costCenter") as |f|}}
                <Input
                  @label={{t "partner.employee.cost_center"}}
                  @value={{f.value}}
                  @onChange={{f.onChange}}
                  @onBlur={{f.onBlur}}
                  @errors={{f.errors}}
                  name="costCenter"
                />
              {{/let}}
            </div>
          </:body>
        </Section>

        {{! Eligibility Section }}
        <Section>
          <:header>{{t "partner.employee.eligibility" default="Eligibility"}}</:header>
          <:body>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              {{#let (form.field "eligibilityStartDate") as |f|}}
                <div class="tio-col-extra-tight">
                  <label class="tio-text-sm-medium tio-fg-primary">{{t
                      "partner.employee.eligibility_start_date"
                    }}</label>
                  <div class="relative">
                    {{! @glint-expect-error: EmberFlatpickr type overrides}}
                    <EmberFlatpickr
                      @date={{or f.value ""}}
                      @onChange={{fn this.onDateChange f.onChange}}
                      @dateFormat="Y-m-d"
                      @altInput={{true}}
                      @altFormat="F j, Y"
                      @disableMobile={{true}}
                      placeholder={{t "common.date_placeholder"}}
                      class="tio-form-input"
                    />
                    <div
                      class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none tio-fg-disabled"
                    >
                      <PhCalendarBlank @size="20" />
                    </div>
                  </div>
                  {{#if f.errors.length}}
                    <p class="text-sm tio-fg-danger-h">{{join f.errors ", "}}</p>
                  {{/if}}
                </div>
              {{/let}}
              {{#let (form.field "eligibilityEndDate") as |f|}}
                <div class="tio-col-extra-tight">
                  <label class="tio-text-sm-medium tio-fg-primary">{{t
                      "partner.employee.eligibility_end_date"
                    }}</label>
                  <div class="relative">
                    {{! @glint-expect-error: EmberFlatpickr type overrides}}
                    <EmberFlatpickr
                      @date={{or f.value ""}}
                      @onChange={{fn this.onDateChange f.onChange}}
                      @dateFormat="Y-m-d"
                      @altInput={{true}}
                      @altFormat="F j, Y"
                      @disableMobile={{true}}
                      placeholder={{t "common.date_placeholder"}}
                      class="tio-form-input"
                    />
                    <div
                      class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none tio-fg-disabled"
                    >
                      <PhCalendarBlank @size="20" />
                    </div>
                  </div>
                  {{#if f.errors.length}}
                    <p class="text-sm tio-fg-danger-h">{{join f.errors ", "}}</p>
                  {{/if}}
                </div>
              {{/let}}
            </div>
          </:body>
        </Section>

        {{! Custom Attributes Section }}
        {{#if @model.company.companySetting.employeeCustomAttributes.length}}
          <Section>
            <:header>{{t
                "partner.employee.custom_attributes"
                default="Custom Attributes"
              }}</:header>
            <:body>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {{#each @model.company.companySetting.employeeCustomAttributes as |attribute|}}
                  {{#if attribute.custom}}
                    <Input
                      @label={{attribute.field}}
                      @value={{get this.customAttributes attribute.field}}
                      @onChange={{fn this.saveCustomAttr attribute.field}}
                    />
                  {{/if}}
                {{/each}}
              </div>
            </:body>
          </Section>
        {{/if}}

        {{! Form Actions }}
        <div class="tio-flex-end gap-3 pt-4">
          <Button @intent="danger" @appearance="outlined" {{on "click" this.cancel}}>
            {{t "partner.employee.cancel"}}
          </Button>
          <Button @intent="primary" type="submit" @isDisabled={{form.isSubmitting}}>
            {{t "partner.employee.create_employee"}}
          </Button>
        </div>
      </VStack>
    </Form>
  </template>
}
